import { mat4, vec2, vec4 } from "gl-matrix"
import { Texture } from "@/core/backends/webgl/texture"
import { ImageState } from "@/core/image-state"
import { WebGLBackend } from "@/core/backends/webgl-backend"
import { clamp } from "@/core/utils/math"

export class Viewport {
	backend: WebGLBackend

	texture: Texture | null = null

	// Static state
	offset: vec2 = [0, 0]
	relativeZoom = 1 // fit
	viewportRect: vec4 = [0, 0, 1, 1]
	isCropView = false

	// Computed state
	imageScale = 1
	croppedImageScale = 1
	fitScale = 1
	croppedFitScale = 1

	viewMatrix = mat4.create()
	cropViewMatrix = mat4.create()
	cropViewMatrixInv = mat4.create()

	viewportSize = vec2.create()
	textureSize = vec2.create()

	constructor(backend: WebGLBackend) {
		this.backend = backend
	}

	computeState(imageState: ImageState) {
		const textureWidth = imageState.originalImageSize[0]
		const textureHeight = imageState.originalImageSize[1]
		const viewportWidth = this.backend.canvas.width * this.viewportRect[2]
		const viewportHeight = this.backend.canvas.height * this.viewportRect[3]

		vec2.set(this.viewportSize, viewportWidth, viewportHeight)
		vec2.set(this.textureSize, textureWidth, textureHeight)

		this.fitScale = this.scaleToFit(imageState.imageSize)
		this.croppedFitScale = this.scaleToFit(imageState.croppedImageSize)
		this.imageScale = this.fitScale * this.relativeZoom
		this.croppedImageScale = this.croppedFitScale * this.relativeZoom
		this.offset = this.limitOffset(imageState)
		this.viewMatrix = this.getViewMatrix(imageState)

		mat4.multiply(this.cropViewMatrix, this.viewMatrix, imageState.cropMatrix)
		mat4.invert(this.cropViewMatrixInv, this.cropViewMatrix)
	}

	setCropView(isCropView: boolean) {
		this.isCropView = isCropView
	}

	setTexture(texture: Texture | null) {
		this.texture = texture
	}

	setViewportRect(x: number, y: number, width: number, height: number) {
		vec4.set(this.viewportRect, x, y, width, height)
	}

	clear() {
		this.setViewport()
		this.backend.setFramebuffer(null)
		this.backend.clear()
	}

	render(imageState: ImageState, renderCallback: (viewport: Viewport) => void) {
		if (!this.texture) {
			return
		}

		this.computeState(imageState)
		this.setViewport()

		this.backend.setFramebuffer(null)
		this.backend.clear()

		renderCallback(this)

		this.backend.enableScissor(false)
	}

	private limitOffset(imageState: ImageState): vec2 {
		const [x, y] = this.offset

		const imageSize = imageState.croppedImageSize
		const imageWidth = imageSize[0] * this.croppedImageScale
		const imageHeight = imageSize[1] * this.croppedImageScale

		const limitX = (imageWidth / this.viewportSize[0]) * this.viewportRect[2]
		const limitY = imageHeight / this.viewportSize[1]

		if (!limitX || !limitY) {
			return this.offset
		}

		return [clamp(x, -limitX, limitX), clamp(y, -limitY, limitY)]
	}

	private setViewport() {
		const x = this.backend.canvas.width * this.viewportRect[0]
		const y = this.backend.canvas.height * this.viewportRect[1]
		const width = this.backend.canvas.width * this.viewportRect[2]
		const height = this.backend.canvas.height * this.viewportRect[3]

		this.backend.enableScissor(true)
		this.backend.setScissor(x, y, width, height)
		this.backend.setViewport(x, y, width, height)
	}

	private scaleToFit(imageSize: vec2) {
		const [cw, ch] = this.viewportSize
		const [iw, ih] = imageSize
		return Math.min(cw / iw, ch / ih)
	}

	private getViewMatrix(imageState: ImageState) {
		const [cw, ch] = this.viewportSize
		const [iw, ih] = imageState.croppedImageSize
		const [x, y] = this.offset
		const vw = this.viewportRect[2]
		const vh = this.viewportRect[3]
		const imageScale = this.isCropView ? this.imageScale : this.croppedImageScale

		const mat = this.viewMatrix

		mat4.ortho(mat, cw / -2, cw / 2, ch / 2, ch / -2, -1, 1)
		mat4.translate(mat, mat, [((x / vw) * cw) / 2, ((y / vh) * ch) / 2, 0])
		mat4.scale(mat, mat, [(iw / 2) * imageScale, (ih / 2) * imageScale, 1])

		return mat
	}
}

// convert pointer coord from pixel space to world space (-1, 1)
export function unproject(pixelResolution: vec2, pixelCoord: vec2, matrix: mat4): vec2 {
	const x = (pixelCoord[0] / pixelResolution[0]) * 2 - 1
	const y = (pixelCoord[1] / pixelResolution[1]) * 2 - 1

	const worldCoord: vec2 = [x, -y]

	vec2.transformMat4(worldCoord, worldCoord, matrix)

	return worldCoord
}

// convert point in world space to screen space coord
export function project(pixelResolution: vec2, worldCoord: vec2, matrix: mat4): vec2 {
	vec2.transformMat4(worldCoord, worldCoord, matrix)
	if (Number.isNaN(worldCoord[0]) || Number.isNaN(worldCoord[1])) {
		worldCoord[0] = 0
		worldCoord[1] = 0
	}
	const x = worldCoord[0] * 0.5 + 0.5
	const y = worldCoord[1] * 0.5 + 0.5

	// prettier-ignore
	return [
		x * pixelResolution[0],
		(1 - y) * pixelResolution[1]
	]
}
